/**
 * FedRampBuildChat Component
 *
 * Handles the chat interface for the TAP Build Agent in FedRamp projects.
 * This agent combines messages from multiple agents: Forms, Workflow, Administration, Report, and Queue.
 */

import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';


import {
  Icon,
  Text,
  Button,
  ChatMessage,
  ChatInput,
} from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import type { ITokenUsage, IDevelopmentTiming } from '@ui-library/TokenUsageTooltip/TokenUsageTooltip.type';

import api, { authenticatedFetch } from '../../utils/axios';
import { formatTimestamp } from '../../utils/dateUtils';
import { useAuth } from '../../contexts/AuthContext';
import { tapBuildWebsocketManager, TapBuildWebSocketMessage } from '../../services/tapBuildWebsocketService';
import { IfHasPermission } from '../IfHasPermission';
import { Permissions } from '../../constants';
import { ChatNavigator, useChatIndex, useChatWindow } from '../ChatNavigator';
import { ChatLoadingIndicator } from '../chat/ChatLoadingIndicator';

import type { Attachment } from '../../types/chat';
import type { TapAgentMessage, FedRampBuildChatProps } from './FedRampBuildChat.types';

// DOM contract shared with the conversation navigator: each rendered message
// is wrapped in an element with id="message-<id>"
const getMessageElement = (id: string) => document.getElementById(`message-${id}`);

// Map TapAgentMessage token_usage to ITokenUsage format
const mapTapAgentTokenUsage = (message: TapAgentMessage): ITokenUsage | undefined => {
  if (!message.token_usage) return undefined;

  return {
    inputTokens: message.token_usage.input_tokens,
    outputTokens: message.token_usage.output_tokens,
    cacheCreationInputTokens: 0,
    cacheReadInputTokens: 0,
    costs: message.token_usage.costs ? {
      input_cost: message.token_usage.costs.input_cost,
      output_cost: message.token_usage.costs.output_cost,
      cache_write_cost: 0,
      cache_read_cost: 0,
      total_cost: message.token_usage.costs.total_cost,
    } : undefined
  };
};

// Map TapAgentMessage timing to IDevelopmentTiming format
const mapTapAgentTiming = (message: TapAgentMessage): IDevelopmentTiming | undefined => {
  if (!message.duration_seconds) return undefined;

  return {
    duration_seconds: message.duration_seconds || 0,
  };
};

export const FedRampBuildChat: React.FC<FedRampBuildChatProps> = ({
  project,
  projectId,
  onLoadingChange,
  onCostUpdate,
  onAgentTypeDetected
}) => {
  const [messages, setMessages] = useState<TapAgentMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [abortController, setAbortController] = useState<AbortController | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [hasUserScrolledUp, setHasUserScrolledUp] = useState(false);
  const [thinkingAction, setThinkingAction] = useState<string>('');
  const isLinked = project?.tap_url !== null;

  // Pagination state
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // Downward pagination (loading newer messages below a historical window —
  // e.g. after a conversation-navigator jump into the middle of the chat)
  const [hasMoreNewer, setHasMoreNewer] = useState(false);
  const [isLoadingNewer, setIsLoadingNewer] = useState(false);

  // Ref to track current isLoading state to avoid closure issues in WebSocket callbacks
  const isLoadingRef = useRef(isLoading);
  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  // Ref to prevent multiple simultaneous message loads
  const isLoadingMessagesRef = useRef(false);
  const pendingRefreshRef = useRef(false);

  // True while THIS window is streaming the response via SSE. The completed
  // WebSocket event branches on it: the sender patches metadata in place (its
  // streamed text must not be touched or refetched), passive windows refetch
  // history. Consume-once: cleared by the completed handler itself, NOT in the
  // SSE finally block — stream_end usually closes the SSE before the completed
  // event arrives, so clearing earlier would make the sender refetch and flash.
  const streamedInThisWindowRef = useRef(false);

  // Polling fallback refs
  const isPollingRef = useRef(false);
  const pollingTimeoutRef = useRef<number | null>(null);

  // Get user for ChatMessage component
  const { user } = useAuth();

  // Check streaming status on mount
  const checkStreamingStatus = useCallback(async () => {
    if (!projectId) return;

    try {
      console.log('🔍 [TapBuildChat] Checking streaming status on mount/reconnect');
      const response = await authenticatedFetch(
        `/api/projects/${projectId}/tap-agent/build/streaming-status`
      );
      const data = await response.json();

      console.log('📊 [TapBuildChat] Streaming status:', data);

      if (data.is_streaming && data.status === 'active') {
        // Still streaming - show "Working..." message
        setMessages(prev => {
          // Find the last assistant message
          let lastAssistantIndex = -1;
          for (let i = prev.length - 1; i >= 0; i--) {
            if (prev[i].role === 'assistant') {
              lastAssistantIndex = i;
              break;
            }
          }
          
          if (lastAssistantIndex !== -1) {
            const updatedMessages = [...prev];
            updatedMessages[lastAssistantIndex] = {
              ...updatedMessages[lastAssistantIndex],
              isThinking: true,
              thinkingMessage: thinkingAction || 'Thinking...',
              isStreaming: true
            };
            return updatedMessages;
          } else {
            // Fallback: Create new message if no assistant message exists
            const streamingMessage: TapAgentMessage = {
              id: `streaming-${data.assistant_message_id || Date.now()}`,
              role: 'assistant',
              content: '',
              created_at: Date.now() / 1000,
              isThinking: true,
              thinkingMessage: thinkingAction || 'Thinking...',
              isStreaming: true
            };
            return [...prev, streamingMessage];
          }
        });
        
        setIsLoading(true);
        
        // Poll again after 5 seconds (optimized for performance)
        if (isPollingRef.current) {
          pollingTimeoutRef.current = window.setTimeout(() => checkStreamingStatus(), 5000);
        }
        
      } else if (data.status === 'completed' || data.status === 'cancelled') {
        // Streaming completed or cancelled - reload messages to get the final response
        console.log('✅ [TapBuildChat] Streaming completed, refreshing messages');
        setIsLoading(false);
        setThinkingAction('');
        fetchMessageHistory();
        isPollingRef.current = false; // Stop polling
        
      } else if (data.status === 'error') {
        // Error occurred
        console.error('❌ [TapBuildChat] Streaming error:', data.error_message);
        setIsLoading(false);
        setThinkingAction('');
        isPollingRef.current = false; // Stop polling
        
        // Show error message
        setMessages(prev => {
          const withoutThinking = prev.filter(msg => !msg.isThinking);
          const errorMsg: TapAgentMessage = {
            id: `error-${Date.now()}`,
            role: 'assistant',
            content: data.error_message || 'An error occurred during generation.',
            created_at: Date.now() / 1000,
            isThinking: false
          };
          return [...withoutThinking, errorMsg];
        });
        
      } else {
        // No active streaming detected via API
        console.log('📡 [TapBuildChat] checkStreamingStatus: No active streaming detected, status=', data.status);
        setIsLoading(false);
        setThinkingAction('');
        isPollingRef.current = false; // Stop polling
      }
    } catch (error) {
      console.error('❌ [TapBuildChat] Error checking streaming status:', error);
      setIsLoading(false);
      isPollingRef.current = false; // Stop polling on error
    }
  }, [projectId, thinkingAction]);

  // WebSocket message handler
  const handleTapBuildWebSocketMessage = useCallback((message: TapBuildWebSocketMessage) => {
    console.log('📨 [TapBuildChat] WebSocket message:', message.type, message);

    switch (message.type) {
      case 'tap_build_user_message_added': {
        const incomingContent = message.data.content;

        setMessages(prev => {
          const lastUserMessage = [...prev].reverse().find(m => m.role === 'user');

          if (lastUserMessage && lastUserMessage.content === incomingContent) {
            console.log('🔄 [TapBuildChat] Message already exists (optimistic), skipping');
            return prev;
          }

          const newUserMessage: TapAgentMessage = {
            id: message.data.message_id || Date.now().toString(),
            role: 'user',
            content: incomingContent || '',
            created_at: Date.now() / 1000,
            user_info: message.data.user_info,
            attachments: message.data.attachments?.length ? message.data.attachments : undefined
          };

          console.log('✅ [TapBuildChat] Adding user message from WebSocket');
          
          return [...prev, newUserMessage];
        });
        break;
      }

      case 'tap_build_streaming_started': {
        // Streaming started (possibly from another window)
        console.log('🔄 [TapBuildChat] Streaming started');

        // Use ref to check current loading state (avoid closure issues)
        if (isLoadingRef.current) {
          console.log('📡 [TapBuildChat] Already loading, skipping');
          return;
        }

        console.log('📡 [TapBuildChat] Setting isLoading=true');
        setIsLoading(true);
        setThinkingAction('');

        // Add "Working..." indicator if not already present (for passive windows)
        setMessages(prev => {
          const hasThinking = prev.some(m => m.role === 'assistant' && !m.content && prev.indexOf(m) === prev.length - 1);
          if (hasThinking) {
            return prev;
          }
          const thinkingMessage: TapAgentMessage = {
            id: `streaming-${message.data.assistant_message_id || Date.now()}`,
            role: 'assistant',
            content: '',
            created_at: Date.now() / 1000,
          };
          
          return [...prev, thinkingMessage];
        });
        break;
      }

      case 'tap_build_streaming_completed': {
        console.log('✅ [TapBuildChat] Streaming completed');
        setIsLoading(false);
        setThinkingAction('');
        isPollingRef.current = false;

        if (streamedInThisWindowRef.current) {
          // This window streamed the response: the full text is already on
          // screen and the message carries its real DB id (stamped by
          // stream_start). Never refetch here — replacing the list would
          // remount every row and flash the text the user is reading. Just
          // patch the completion metadata in place by direct id match.
          streamedInThisWindowRef.current = false;

          const completionData = message.data;
          if (completionData.assistant_message_id) {
            setMessages(prev => prev.map(msg =>
              msg.id === completionData.assistant_message_id
                ? {
                    ...msg,
                    content: msg.content || completionData.content || '',
                    token_usage: completionData.token_usage && Object.keys(completionData.token_usage).length > 0
                      ? completionData.token_usage as TapAgentMessage['token_usage']
                      : msg.token_usage,
                    duration_seconds: completionData.duration_seconds ?? msg.duration_seconds,
                    isThinking: false,
                    isStreaming: false,
                  }
                : msg
            ));
          }
        } else {
          // Passive window (collaborator / second tab): only a thinking bubble
          // is on screen, so a full reload is safe — the response appearing at
          // completion just looks like arrival, and it gets canonical state
          // (real ids, duration, agent type) in one shot.
          fetchMessageHistory();
        }

        // Refresh project cost
        if (projectId && onCostUpdate) {
          api.get(`/projects/${projectId}/stats`).then(response => {
            const totalCost = parseFloat(response.data.total_cost) || 0;
            onCostUpdate(totalCost);
          }).catch(err => {
            console.error('Failed to fetch project cost:', err);
          });
        }
        break;
      }

      case 'tap_build_streaming_cancelled':
        console.log('⚠️ [TapBuildChat] Streaming cancelled');
        setIsLoading(false);
        setThinkingAction('');
        isPollingRef.current = false;

        // Reload messages to get the cancelled message from DB
        fetchMessageHistory();
        break;

      case 'tap_build_streaming_error':
        console.log('❌ [TapBuildChat] Streaming error');
        setIsLoading(false);
        setThinkingAction('');
        isPollingRef.current = false;
        
        // Only auto-scroll if user is already at bottom
        if (!hasUserScrolledUp) {
          setHasUserScrolledUp(false);
        }
        
        // Reload messages to get the error message from DB
        fetchMessageHistory();
        break;

      case 'tap_build_streaming_status': {
        // Initial streaming status on connect (received when passive window connects)
        console.log('📡 [TapBuildChat] Received streaming_status:', message.data);
        if (message.data.is_streaming && message.data.status === 'active') {
          console.log('📡 [TapBuildChat] Active streaming detected - showing loader');
          setIsLoading(true);

          // Show thinking indicator if not already present AND if messages are loaded
          setMessages(prev => {
            const hasThinking = prev.some(m => m.role === 'assistant' && !m.content && prev.indexOf(m) === prev.length - 1);
            if (hasThinking) return prev;

            // Don't add thinking message if messages haven't loaded yet
            if (prev.length === 0) return prev;

            const thinkingMessage: TapAgentMessage = {
              id: `streaming-${message.data.assistant_message_id || Date.now()}`,
              role: 'assistant',
              content: '',
              created_at: Date.now() / 1000,
            };
            
            return [...prev, thinkingMessage];
          });
        }
        break;
      }
    }
  }, [projectId, isLoading, onCostUpdate, hasUserScrolledUp]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      isPollingRef.current = false;
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, [projectId]);

  // Connect to WebSocket
  useEffect(() => {
    if (!projectId) return;

    const token = localStorage.getItem('token') || '';

    console.log('🔌 [TapBuildChat] Connecting to WebSocket for project:', projectId);

    tapBuildWebsocketManager.connect(projectId, token, {
      onMessage: handleTapBuildWebSocketMessage,
      onConnect: () => {
        console.log('✅ [TapBuildChat] WebSocket connected');
        isPollingRef.current = false;
        if (pollingTimeoutRef.current) {
          clearTimeout(pollingTimeoutRef.current);
          pollingTimeoutRef.current = null;
        }
        checkStreamingStatus();
      },
      onReconnect: () => {
        console.log('🔄 [TapBuildChat] WebSocket reconnected, reloading messages');
        isPollingRef.current = false;
        if (pollingTimeoutRef.current) {
          clearTimeout(pollingTimeoutRef.current);
          pollingTimeoutRef.current = null;
        }
        checkStreamingStatus();
        fetchMessageHistory();
      },
      onDisconnect: () => {
        console.log('⚠️ [TapBuildChat] WebSocket disconnected');
        if (isLoading) {
          isPollingRef.current = true;
          checkStreamingStatus();
        }
      },
      onError: (error) => {
        console.error('📡 [TapBuildChat] WebSocket Error:', error);
        if (isLoading) {
          isPollingRef.current = true;
          checkStreamingStatus();
        }
      }
    });

    return () => {
      // Don't disconnect here - let the hook manage its own lifecycle
    };
  }, [projectId, handleTapBuildWebSocketMessage, checkStreamingStatus, isLoading]);

  // Check streaming status on mount
  useEffect(() => {
    if (projectId) {
      checkStreamingStatus();
    }
  }, [projectId, checkStreamingStatus]);

  useEffect(() => {
    fetchMessageHistory(true);
  }, [projectId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Load more messages (pagination)
  const loadMore = useCallback(async (container: HTMLDivElement) => {
    if (isLoadingRef.current || isLoadingMore) return;
    if (!hasMoreMessages || !projectId) return;

    // Get the oldest message ID before starting the load
    const oldestMessage = messages[0];
    if (!oldestMessage || !oldestMessage.id) {
      console.warn('⚠️ No oldest message found for pagination');
      return;
    }

    isLoadingRef.current = true;
    setIsLoadingMore(true);

    const prevScrollHeight = container.scrollHeight;
    const prevScrollTop = container.scrollTop;

    try {
      // Call fetchMessageHistory directly with the oldest message ID
      await fetchMessageHistory(false, 20, oldestMessage.id);
    } finally {
      requestAnimationFrame(() => {
        const newScrollHeight = container.scrollHeight;
        container.scrollTop = newScrollHeight - prevScrollHeight + prevScrollTop;
        isLoadingRef.current = false;
        setIsLoadingMore(false);
      });
    }
  }, [hasMoreMessages, projectId, isLoadingMore, messages]); // fetchMessageHistory is defined below, so we can't include it here

  // Handle scroll to detect if user is at bottom. Pagination triggers live in
  // the chat-window hook (composed onto onScroll in the JSX) so navigator jumps
  // never kick off a page load mid-jump.
  const handleScroll = useCallback(() => {
    const container = chatContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const threshold = 100;

    const atBottom = distanceFromBottom < threshold;
    setIsAtBottom(atBottom);

    // If user scrolled up manually (away from bottom), set the flag
    if (!atBottom && !hasUserScrolledUp) {
      setHasUserScrolledUp(true);
    }

    // If user scrolled back to bottom manually, re-enable auto-scroll
    if (atBottom && hasUserScrolledUp) {
      setHasUserScrolledUp(false);
    }
  }, [hasUserScrolledUp]);

  // Auto-scroll only if user hasn't manually scrolled up AND we're at the true
  // tail — never yank a historical window (navigator jump) back to the bottom.
  useEffect(() => {
    if (!hasUserScrolledUp && !hasMoreNewer) {
      scrollToBottom();
    }
  }, [messages, hasUserScrolledUp, hasMoreNewer]);

  const fetchMessageHistory = async (isInitialLoad = false, limit = 20, beforeMessageId?: string) => {
    if (isLoadingMessagesRef.current) {
      pendingRefreshRef.current = true;
      return;
    }

    isLoadingMessagesRef.current = true;
    pendingRefreshRef.current = false;

    try {
      if (isInitialLoad) {
        setLoadingHistory(true);
        onLoadingChange?.(true);
      }

      // Build query parameters for pagination
      const params = new URLSearchParams();
      if (limit) params.append('limit', limit.toString());
      if (beforeMessageId) params.append('before_message_id', beforeMessageId);

      const response = await api.get(`/projects/${projectId}/tap-agent/build/messages?${params.toString()}`);
      const newMessages = response.data.messages || [];

      // Update hasMore state based on pagination metadata
      setHasMoreMessages(response.data?.pagination?.has_more || false);

      // Handle pagination: prepend vs replace
      if (beforeMessageId) {
        setMessages(prevMessages => [...newMessages, ...prevMessages]);
      } else {
        // Replacing with the latest page — this is the true tail, so nothing
        // newer remains below (clears any historical-window state).
        setHasMoreNewer(false);
        // Just set messages - checkStreamingStatus will handle marking empty messages as thinking.
        // Stagger the entrance animation only on the initial history load; messages
        // added later must animate immediately (see TapAgentMessage.staggerIndex).
        setMessages(
          isInitialLoad
            ? newMessages.map((msg: TapAgentMessage, i: number) => ({ ...msg, staggerIndex: i }))
            : newMessages
        );
      }
    } catch (error) {
      console.error('Failed to fetch TAP build agent history:', error);
    } finally {
      setLoadingHistory(false);
      onLoadingChange?.(false);
      isLoadingMessagesRef.current = false;

      if (pendingRefreshRef.current) {
        pendingRefreshRef.current = false;
        fetchMessageHistory();
      }
    }
  };

  // ── Conversation navigator ────────────────────────────────────────────────
  // Lightweight full-thread index of user turns (id / timestamp / preview).
  const fetchNavIndex = useMemo(() => {
    if (!projectId) return null;
    return async () =>
      (await api.get(`/projects/${projectId}/tap-agent/build/chat-index`)).data.items;
  }, [projectId]);
  const { items: navItems, refresh: refreshNavIndex } = useChatIndex(fetchNavIndex);

  // Re-sync the index when a streaming turn completes (the user turn is
  // persisted with its real DB id by then).
  const prevLoadingRef = useRef(false);
  useEffect(() => {
    if (prevLoadingRef.current && !isLoading) {
      refreshNavIndex();
    }
    prevLoadingRef.current = isLoading;
  }, [isLoading, refreshNavIndex]);

  // Replace the loaded list with a window starting at the target (inclusive).
  const jumpToMessage = useCallback(async (id: string) => {
    if (isLoadingRef.current || !projectId) return null;
    isLoadingRef.current = true;
    setIsLoadingMore(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', '20');
      params.append('after_message_id', id);
      const response = await api.get(`/projects/${projectId}/tap-agent/build/messages?${params.toString()}`);
      const newMessages: TapAgentMessage[] = response.data.messages || [];
      setHasMoreMessages(response.data?.pagination?.has_more_older || false);
      setHasMoreNewer(response.data?.pagination?.has_more_newer || false);
      setMessages(newMessages);
      return { found: newMessages.some(m => m.id === id) };
    } catch (error) {
      console.error('Failed to jump to message:', error);
      return null;
    } finally {
      isLoadingRef.current = false;
      setIsLoadingMore(false);
    }
  }, [projectId]);

  // Append the next newer page below a historical window.
  const loadNewer = useCallback(async () => {
    if (isLoadingRef.current || !projectId || !hasMoreNewer) return;
    const newest = messages[messages.length - 1];
    if (!newest?.id) return;
    isLoadingRef.current = true;
    setIsLoadingNewer(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', '20');
      params.append('after_message_id', newest.id);
      const response = await api.get(`/projects/${projectId}/tap-agent/build/messages?${params.toString()}`);
      const newMessages: TapAgentMessage[] = response.data.messages || [];
      setHasMoreNewer(response.data?.pagination?.has_more_newer || false);
      // after_message_id is inclusive of the ref — dedup by id when appending.
      setMessages(prev => {
        const existing = new Set(prev.map(m => m.id));
        return [...prev, ...newMessages.filter(m => !existing.has(m.id))];
      });
    } catch (error) {
      console.error('Failed to load newer messages:', error);
    } finally {
      isLoadingRef.current = false;
      setIsLoadingNewer(false);
    }
  }, [projectId, hasMoreNewer, messages]);

  // useChatWindow needs a `timestamp` field; map created_at (epoch seconds) to
  // a Date-parseable millisecond value. Reused for the rail's loaded-window props.
  const windowMessages = useMemo(
    () => messages.map(m => ({ ...m, timestamp: (m.created_at ?? 0) * 1000 })),
    [messages],
  );

  const navAdapter = useMemo(() => ({
    jumpToMessage,
    loadOlder: (container: HTMLDivElement) => {
      if (hasMoreMessages && !isLoadingMore && !isLoadingRef.current) {
        loadMore(container);
      }
    },
    loadNewer: () => {
      if (hasMoreNewer && !isLoadingNewer && !isLoadingRef.current) {
        loadNewer();
      }
    },
  }), [jumpToMessage, hasMoreMessages, isLoadingMore, loadMore, hasMoreNewer, isLoadingNewer, loadNewer]);

  const chatWindow = useChatWindow({
    containerRef: chatContainerRef,
    messages: windowMessages,
    getMessageElement,
    adapter: navAdapter,
    // A jump target that can't be loaded means the index is stale (message gone)
    onStaleTarget: refreshNavIndex,
  });
  // ──────────────────────────────────────────────────────────────────────────

  const handleStopStreaming = async () => {
    console.log('🛑 Stopping TAP build agent conversation...');

    if (abortController) {
      abortController.abort();
      setAbortController(null);
    }

    if (projectId) {
      try {
        await api.post(`/projects/${projectId}/tap-agent/build/stop`);

        console.log('📥 Reloading TAP build messages after stop...');
        await fetchMessageHistory();
      } catch (error) {
        console.error('❌ Failed to stop TAP build stream:', error);
      }
    }

    setIsLoading(false);
    setThinkingAction('');
    console.log('✅ TAP build agent conversation stopped');
  };

  const handleSendMessage = async (messageContent?: string, attachments?: Attachment[]) => {
    const userMessage = (messageContent || input).trim();
    if (!userMessage || isLoading) return;

    setInput('');
    setIsLoading(true);

    // Always scroll to bottom when user sends a new message
    setHasUserScrolledUp(false);

    const controller = new AbortController();
    setAbortController(controller);

    // Generate unique IDs for this conversation turn. These are temporary:
    // the stream's first event (stream_start) carries the real DB ids, which
    // get stamped onto the messages while they're still in thinking state.
    const userMessageId = `user-${Date.now()}`;
    const assistantMessageId = `assistant-${Date.now()}`;
    let currentUserId = userMessageId;
    let currentAssistantId = assistantMessageId;

    // Add user message to UI immediately (optimistic update)
    const optimisticUserMessage: TapAgentMessage = {
      id: userMessageId,
      role: 'user',
      content: userMessage,
      created_at: Date.now() / 1000,
      attachments: attachments && attachments.length > 0 ? attachments : undefined,
    };
    setMessages(prev => [...prev, optimisticUserMessage]);

    // Add thinking message for assistant
    const thinkingMessage: TapAgentMessage = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      created_at: Date.now() / 1000,
    };
    setMessages(prev => [...prev, thinkingMessage]);

    // Variable to store detected agent type
    let detectedAgentType: string | null = null;

    // Mark this window as the streaming one — the completed WebSocket event
    // uses this to patch in place instead of refetching (consume-once there).
    streamedInThisWindowRef.current = true;

    try {
      const baseURL = import.meta.env.VITE_API_URL || '';

      const response = await authenticatedFetch(
        `${baseURL}/api/projects/${projectId}/tap-agent/build/chat`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            query: userMessage,
            attachments: attachments || [],
          }),
          signal: controller.signal
        }
      );

      if (!response.ok) {
        // Surface the backend's error detail (e.g. unsupported attachment type)
        let errorDetail = '';
        try {
          const errorBody = await response.json();
          errorDetail = errorBody?.detail || '';
        } catch {
          // Non-JSON error body — fall back to the generic message
        }
        throw new Error(errorDetail || `HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('Response body is null');
      }

      let buffer = '';
      let assistantContent = '';
      let firstChunkReceived = false;

      console.log('🚀 Starting TAP Build Agent request stream...');

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          console.log('✅ Stream completed');
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          // Handle SSE comment lines (heartbeats)
          if (line.startsWith(':')) {
            continue;
          }

          if (line.trim()) {
            // Handle NDJSON chunks
            try {
              const data = JSON.parse(line);

              // First stream event: the real DB ids for this turn. Stamp them
              // onto the optimistic messages while they're still in thinking
              // state (no text on screen yet), so no id reconciliation is ever
              // needed once content is visible.
              if (data.type === 'stream_start') {
                const realUserId = data.user_message_id;
                const realAssistantId = data.assistant_message_id;
                // Capture the ids to match on BEFORE updating the trackers —
                // the setMessages updater runs later and must target the old ids.
                const prevUserId = currentUserId;
                const prevAssistantId = currentAssistantId;
                setMessages(prev => prev.map(msg => {
                  if (realUserId && msg.id === prevUserId) return { ...msg, id: realUserId };
                  if (realAssistantId && msg.id === prevAssistantId) return { ...msg, id: realAssistantId };
                  return msg;
                }));
                if (realUserId) currentUserId = realUserId;
                if (realAssistantId) currentAssistantId = realAssistantId;
                continue;
              }

              // Handle error type
              if (data.type === 'error') {
                console.error('❌ TAP Build Agent error:', data);
                // No completed event will consume the flag — clear it so a
                // later completion (e.g. another user's stream) refetches.
                streamedInThisWindowRef.current = false;
                const errorMessage = data.message || 'An error occurred. Please try again.';

                // Replace thinking message with error message
                setMessages(prev => prev.map(msg =>
                  msg.id === currentAssistantId
                    ? { ...msg, content: errorMessage }
                    : msg
                ));
                
                setIsLoading(false);
                setThinkingAction('');
                break;
              }

              if (data.type === 'RESPONSE_TOKEN') {
                const token = data.message || '';
                assistantContent += token;

                if (!firstChunkReceived) {
                  firstChunkReceived = true;
                }

                // Update assistant message in place
                setMessages(prev => prev.map(msg =>
                  msg.id === currentAssistantId
                    ? { ...msg, content: assistantContent }
                    : msg
                ));
              } else if (data.type === 'ACTION') {
                console.log(`📊 ${data.type}:`, data.message);
                setThinkingAction(data.message || '');
              } else if (data.type === 'TOOL_CALL') {
                // Logged only — TOOL_CALL must not drive the progress indicator
                console.log(`📊 ${data.type}:`, data.message);
              } else if (data.type === 'FINAL_RESPONSE' && data.is_complete) {
                console.log('✅ TAP Build Agent completed');

                // Update final message with complete content and metadata
                setMessages(prev => prev.map(msg =>
                  msg.id === currentAssistantId
                    ? {
                      ...msg,
                      // Keep the streamed content — replacing text the user is
                      // already reading causes a visible change. The payload is
                      // only a fallback when nothing was streamed.
                      content: assistantContent || data.message,
                      token_usage: data.utilization ? {
                        input_tokens: data.utilization.input_tokens,
                        output_tokens: data.utilization.output_tokens,
                        total_tokens: data.utilization.total_tokens,
                        costs: {
                          input_cost: data.utilization.input_cost,
                          output_cost: data.utilization.output_cost,
                          total_cost: data.utilization.input_cost + data.utilization.output_cost
                        }
                      } : undefined
                    }
                    : msg
                ));

                if (data.last_agent) {
                  const agentTypeMap: Record<string, string> = {
                    'DataFormAgent': 'forms',
                    'DataObjectAgent': 'data_model',
                  };
                  detectedAgentType = agentTypeMap[data.last_agent] || null;
                  
                  // Extract business_key if available for forms
                  if (data.last_agent === 'DataFormAgent' && data.data_form?.business_key) {
                    console.log('📋 [TapBuildChat] Form business_key detected:', data.data_form.business_key);
                    // Store business_key to pass to parent
                    detectedAgentType = `forms:${data.data_form.business_key}`;
                  }
                }

                // Refresh project cost
                if (projectId && onCostUpdate) {
                  api.get(`/projects/${projectId}/stats`).then(response => {
                    const totalCost = parseFloat(response.data.total_cost) || 0;
                    onCostUpdate(totalCost);
                  }).catch(err => {
                    console.error('Failed to fetch project cost:', err);
                  });
                }
              }
            } catch (e) {
              // Not JSON, skip
            }
          }
        }
      }

    } catch (error: any) {
      console.error('Failed to send TAP build agent message:', error);

      // Stream didn't complete normally in this window — clear the flag so
      // future completions aren't mistaken for this window's stream.
      streamedInThisWindowRef.current = false;

      if (error.name !== 'AbortError') {
        // Replace thinking message with error message
        setMessages(prev => prev.map(msg =>
          msg.id === currentAssistantId
            ? {
              ...msg,
              content: error.message || 'Failed to process your request. Please try again.',
            }
            : msg
        ));
      } else {
        // User aborted - remove the thinking message
        setMessages(prev => prev.filter(msg => msg.id !== currentAssistantId));
      }
    } finally {
      setIsLoading(false);
      setAbortController(null);

      // After streaming is complete and loading is done, trigger panel opening
      if (detectedAgentType && onAgentTypeDetected) {
        onAgentTypeDetected(detectedAgentType);
      }
    }
  };

  if (loadingHistory) {
    return null;
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 bg-bg-primary">
      {/* Messages Area Wrapper */}
      <div className="flex-1 min-h-0 relative">
      <div
        ref={chatContainerRef}
        onScroll={(e) => { handleScroll(); chatWindow.onScroll(e); }}
        onWheel={chatWindow.onWheel}
        onPointerDown={chatWindow.armUserScroll}
        onTouchStart={chatWindow.armUserScroll}
        className="h-full flex flex-col items-center py-bg-v2-md overflow-y-auto modern-scrollbar-spaced"
      >
        <div className="flex flex-col gap-bg-v2-xl w-full max-w-[800px] relative px-4">
          {/* Messages */}
          {messages.map((message, index) => {
            const isLastMessage = index === messages.length - 1;
            const isStreamingMessage = isLastMessage && isLoading && message.role === 'assistant';

            return (
              <div
                key={message.id}
                id={`message-${message.id}`}
                className="animate-fade-in-up-smooth opacity-0"
                style={{
                  // Stagger only messages from the initial history load. Anything
                  // added later (sent, streamed, id-stamped) animates immediately —
                  // an index-based delay would leave remounted rows at opacity 0.
                  animationDelay: `${(message.staggerIndex ?? 0) * 0.05}s`,
                  animationFillMode: 'forwards'
                }}
              >
                <ChatMessage
                  message={{
                   ...message,
                    timestamp: formatTimestamp(new Date(message.created_at * 1000), {
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true
                    }) as any,
                    tokenUsage: mapTapAgentTokenUsage(message),
                    developmentTiming: mapTapAgentTiming(message),
                    isThinking: isStreamingMessage && !message.content,
                    thinkingMessage: isStreamingMessage && !message.content ? (thinkingAction || 'Thinking...') : undefined,
                    isStreaming: isStreamingMessage && message.content.length > 0,
                  }}
                  currentUser={user}
                  project={null}
                  isDesignMode={false}
                  isFullWidthMessages={false}
                />
              </div>
            );
          })}

          <div ref={messagesEndRef} />
        </div>

      </div>

        {/* Pagination indicators - overlaid siblings of the scroll container */}
        {isLoadingMore && <ChatLoadingIndicator edge='top' />}
        {isLoadingNewer && <ChatLoadingIndicator edge='bottom' />}

        {/* Scroll to Bottom Button */}
        {(!isAtBottom || hasMoreNewer) && (
          <div className="absolute bottom-bg-v2-sm left-1/2 -translate-x-1/2 z-10 pointer-events-none">
            <Button
              onClick={async () => {
                setHasUserScrolledUp(false);
                // A historical window's loaded bottom is not the true tail —
                // reload the latest page first, then jump to it.
                if (hasMoreNewer) {
                  await fetchMessageHistory();
                  requestAnimationFrame(() =>
                    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' })
                  );
                } else {
                  messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              variant="ghost"
              className="!w-9 !h-9 !rounded-bg-v2-full !bg-bg-primary border border-border-secondary !shadow-bg-v2-md hover:!bg-bg-secondary pointer-events-auto !p-bg-v2-none"
              title="Scroll to bottom"
              label={<Icon name={IconName.ArrowDown} size="sm" variant="secondary" />}
            />
          </div>
        )}

        {/* Conversation Navigator - right-edge minimap of user turns */}
        <ChatNavigator
          items={navItems}
          onSelect={chatWindow.jumpTo}
          containerRef={chatContainerRef}
          getMessageElement={getMessageElement}
          loadedCount={windowMessages.length}
          oldestLoadedId={windowMessages[0]?.id}
          newestLoadedId={windowMessages[windowMessages.length - 1]?.id}
        />
      </div>

      {/* Chat Input */}
      <IfHasPermission
        permissions={project?.permissions}
        requires={Permissions.UpdateProjectPermission}
      >
        <div className="flex justify-center pt-bg-v2-lg pb-bg-v2-3xl px-bg-v2-xl bg-bg-primary">
            <ChatInput
              value={input}
              onChange={setInput}
              onSend={handleSendMessage}
              onStop={handleStopStreaming}
              placeholder={!isLinked ? "Please link your TAP site to enable the Build Agent" : "Ask about forms, workflows, administration, reports, or queues..."}
              disabled={isLoading || !isLinked}
              isStreaming={isLoading}
              isSending={isLoading}
              projectId={projectId || undefined}
              showAttachmentButton={true}
              requireTextToSend={true}
              allowedDocumentExtensions={['.pdf']}
            />
          </div>
      </IfHasPermission>
    </div>
  );
};